import React, { useEffect, useState, useCallback, useMemo } from "react";
import useScreenWidth from "./hooks/useScreenWidth";
import DisplayDNA from "./components/DisplayDNA.js";
import styles from "./App.module.css";
import BottomFindBar from "./components/BottomFindBar.js";
import MenuDrawer from "./components/MenuDrawer";
import FeatureModal from "./components/FeatureModal";
import {
  ensureWasm,
  parseFastaToJsonSync,
  complementSync,
  findOrfsBothStrands, // BOTH strands, end EXCLUSIVE
} from "./wasmFunctions";

/** ===== Feature transforms (unchanged) ===== */
function applyDeleteToFeature(f, ds, de) {
  const fs = f.start;
  const fe = f.end;
  const delLen = de >= ds ? de - ds + 1 : 0;
  if (delLen === 0) return { ...f };

  if (fe < ds) return { ...f };
  if (fs > de) return { ...f, start: fs - delLen, end: fe - delLen };
  if (fs >= ds && fe <= de) return null;
  if (fs < ds && fe > de) return { ...f, start: fs, end: fe - delLen };
  if (fs < ds && fe >= ds && fe <= de) return { ...f, start: fs, end: ds - 1 };
  if (fs >= ds && fs <= de && fe > de)
    return { ...f, start: ds, end: fe - delLen };
  return { ...f };
}
function applyInsertToFeature(f, insAt, insLen) {
  if (insLen === 0) return { ...f };
  let { start, end } = f;

  if (insAt < start) return { ...f, start: start + insLen, end: end + insLen };
  if (insAt >= start && insAt <= end + 1) return { ...f, end: end + insLen };
  return { ...f };
}
function transformFeaturesOnEdit(features, selStart, selEnd, insLen) {
  let out = [];
  for (const f of features) {
    const afterDel = applyDeleteToFeature(f, selStart, selEnd);
    if (afterDel) out.push(afterDel);
  }
  if (insLen > 0) {
    out = out.map((f) => applyInsertToFeature(f, selStart, insLen));
  }
  return out;
}

function App() {
  const screenWidth = useScreenWidth();
  const [DNAobj, setDNAobj] = useState(null);
  const [mainSequence, setMainSequence] = useState("");
  const [complementarySequence, setComplementarySequence] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [selectedRange, setSelectedRange] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [findOpen, setFindOpen] = useState(false);

  // Features & ORFs
  const [features, setFeatures] = useState([]);
  const [featureModalOpen, setFeatureModalOpen] = useState(false);
  const [orfs, setOrfs] = useState([]);

  // --- Sizing knobs ---
  const charWidthPx = 10;
  const letterSpacingPx = 2;
  const labelWPx = 52;
  const strandPadPx = 6;
  const pageSidePaddingPx = 39;
  const gridGapPx = 0;
  const lineGapPx = 6;

  const targetAdvance = charWidthPx + letterSpacingPx;
  const availablePx =
    screenWidth -
    2 * pageSidePaddingPx -
    2 * labelWPx -
    2 * strandPadPx -
    2 * gridGapPx;

  const maxChars = Math.max(
    1,
    Math.floor((availablePx + letterSpacingPx) / targetAdvance)
  );
  const lineLength = Math.max(30, Math.floor(maxChars / 30) * 30);

  const targetTextWidthPx =
    lineLength * charWidthPx + Math.max(0, lineLength - 1) * letterSpacingPx;

  const wrapperWidthPx =
    2 * labelWPx + 2 * strandPadPx + 2 * gridGapPx + targetTextWidthPx;

  const styleVars = useMemo(
    () => ({
      "--char-w": `${charWidthPx}px`,
      "--ls": `${letterSpacingPx}px`,
      "--label-w": `${labelWPx}px`,
      "--sp-pad": `${strandPadPx}px`,
      "--wrapper-w": `${wrapperWidthPx}px`,
      "--grid-gap": `${gridGapPx}px`,
      "--line-gap": `${lineGapPx}px`,
    }),
    [
      charWidthPx,
      letterSpacingPx,
      labelWPx,
      strandPadPx,
      wrapperWidthPx,
      gridGapPx,
      lineGapPx,
    ]
  );

  useEffect(() => {
    ensureWasm();
  }, []);

  // --- Sequence edits (delete/paste) ---
  const handleEdit = (editType, payload) => {
    if (!selectedRange) return;
    let { start, end } = selectedRange;
    if (start > end) [start, end] = [end, start];

    let newSeq = mainSequence;
    let newRange = null;
    let newFeatures = features;

    if (editType === "delete" || editType === "cut") {
      newSeq = mainSequence.slice(0, start) + mainSequence.slice(end + 1);
      newFeatures = transformFeaturesOnEdit(features, start, end, 0);
      const caret = Math.max(0, start - 1);
      if (newSeq.length > 0) newRange = { start: caret, end: caret };
    }

    if (editType === "paste" && typeof payload === "string") {
      const cleaned = payload.replace(/[^ATGC]/gi, "").toUpperCase();
      const insLen = cleaned.length;
      if (insLen > 0) {
        newSeq =
          mainSequence.slice(0, start) + cleaned + mainSequence.slice(end + 1);
        newFeatures = transformFeaturesOnEdit(features, start, end, insLen);
        newRange = { start, end: start + insLen - 1 };
      }
    }

    setMainSequence(newSeq);
    setComplementarySequence(complementSync(newSeq));
    setFeatures(newFeatures);
    setSelectedRange(newRange);
    setOrfs([]); // ORFs stale after edits
  };

  // --- File I/O ---
  const handleFileUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      try {
        const result = parseFastaToJsonSync(text);
        const sequence = (result.records?.[0]?.sequence || "").toUpperCase();
        const comp = complementSync(sequence);
        setMainSequence(sequence);
        setComplementarySequence(comp);
        setDNAobj(result);
        setFeatures([]);
        setOrfs([]);
      } catch (err) {
        console.error("Failed to parse FASTA:", err);
      }
    };
    reader.readAsText(file);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  }, []);
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);

  // ----- ORF actions (BOTH strands; EXCLUSIVE end) -----
  function normalizeOrfs(rawOrfs, offset = 0) {
    const out = [];
    const ts = Date.now();
    for (let i = 0; i < rawOrfs.length; i++) {
      const r = rawOrfs[i] || {};
      const s0 = r.start ?? 0;
      const e0 = r.endExcl ?? r.end ?? 0; // prefer endExcl
      const s = Math.min(s0, e0) + offset;
      const eExcl = Math.max(s0, e0) + offset;

      const direction = (r.frame ?? 0) >= 3 ? "reverse" : "forward";
      const strand = direction === "forward" ? "top" : "bottom";
      out.push({
        id: `orf-${ts}-${i}-${Math.random().toString(36).slice(2)}`,
        start: s,
        endExcl: eExcl, // EXCLUSIVE
        frame: r.frame ?? 0,
        strand,
        direction,
      });
    }
    return out;
  }

  const runFindOrfsWhole = async () => {
    if (!mainSequence) return;
    const raw = await findOrfsBothStrands(mainSequence);
    setOrfs(normalizeOrfs(raw, 0));
    setMenuOpen(false);
  };

  const runFindOrfsSelection = async () => {
    if (!mainSequence || !selectedRange) return;
    let { start, end } = selectedRange;
    if (start > end) [start, end] = [end, start];
    const subseq = mainSequence.slice(start, end + 1);
    const raw = await findOrfsBothStrands(subseq);
    setOrfs(normalizeOrfs(raw, start));
    setMenuOpen(false);
  };

  const menuItems = [
    {
      id: "find",
      label: "Find sequence",
      icon: "🔎",
      onClick: () => {
        setMenuOpen(false);
        setFindOpen(true);
      },
    },
    {
      id: "addFeature",
      label: "Add feature from selection",
      icon: "🏷️",
      onClick: () => {
        setMenuOpen(false);
        setFeatureModalOpen(true);
      },
    },
    {
      id: "orfAll",
      label: "Find ORFs (whole sequence)",
      icon: "🧬",
      onClick: runFindOrfsWhole,
    },
    {
      id: "orfSel",
      label: "Find ORFs (from selection)",
      icon: "🧬↦",
      onClick: runFindOrfsSelection,
    },
    {
      id: "copy",
      label: "Copy selection",
      icon: "📄",
      onClick: async () => {
        if (!selectedRange) return;
        let { start, end } = selectedRange;
        if (start > end) [start, end] = [end, start];
        const txt = mainSequence.slice(start, end + 1);
        try {
          await navigator.clipboard.writeText(txt);
        } catch {}
      },
    },
    {
      id: "paste",
      label: "Paste",
      icon: "📋",
      onClick: async () => {
        const txt = await navigator.clipboard.readText().catch(() => "");
        if (txt) handleEdit("paste", txt);
      },
    },
    {
      id: "delete",
      label: "Delete selection",
      icon: "✂️",
      onClick: () => handleEdit("delete"),
    },
  ];

  const handleAddFeature = ({ name, color, direction, strand }) => {
    if (!selectedRange) return;
    let { start, end } = selectedRange;
    if (start > end) [start, end] = [end, start];

    const feature = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      start,
      end,
      name: name?.trim() || "Feature",
      color,
      direction,
      strand,
    };
    setFeatures((prev) => [...prev, feature]);
    setFeatureModalOpen(false);
  };

  return (
    <div className={styles.fullscreenContainer}>
      <header className={styles.header}>
        <h1>TAGCAT DNA editor</h1>
        {DNAobj ? null : (
          <input
            type="file"
            accept=".txt,.fasta"
            onChange={(e) => handleFileUpload(e.target.files[0])}
          />
        )}
        <div className={styles.headerActions}>
          <button
            className={styles.pillBtn}
            onClick={() => setMenuOpen(true)}
            type="button"
          >
            ☰ Menu
          </button>
        </div>
      </header>

      <main className={styles.sequenceView}>
        {DNAobj ? (
          <DisplayDNA
            fwStrand={mainSequence}
            rwStrand={complementarySequence}
            lineLength={lineLength}
            showLeftLabels={true}
            showRightLabels={true}
            onSelect={setSelectedRange}
            selectedRange={selectedRange}
            onEdit={handleEdit}
            styleVars={styleVars}
            targetTextWidthPx={targetTextWidthPx}
            features={features}
            orfs={orfs}
          />
        ) : (
          <div
            className={`${styles.dropZone} ${
              isDragging ? styles.dragging : ""
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            {isDragging
              ? "Drop your file here..."
              : "Drag & drop a FASTA file here"}
          </div>
        )}
      </main>

      {DNAobj && (
        <MenuDrawer
          isOpen={menuOpen}
          onClose={() => setMenuOpen(false)}
          items={menuItems}
          title="Quick Actions"
        />
      )}

      {DNAobj && (
        <>
          <BottomFindBar
            isOpen={findOpen}
            onClose={() => setFindOpen(false)}
            haystack={mainSequence}
            onSelectRange={setSelectedRange}
          />
          <FeatureModal
            isOpen={featureModalOpen}
            onClose={() => setFeatureModalOpen(false)}
            onSubmit={handleAddFeature}
            selection={selectedRange}
          />
        </>
      )}
    </div>
  );
}

export default App;
